import { useRef, useState } from "react";
import {
  Box,
  ButtonGroup,
  Button as MuiButton,
  MenuItem,
  ListItemText,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  SxProps,
  Theme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ExportFormat } from "@/types";

interface ExportOption {
  format: ExportFormat;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

const exportOptions: ExportOption[] = [
  {
    format: "excel",
    label: "Export as Excel",
  },
  {
    format: "pdf",
    label: "Export as PDF",
    disabled: true,
    disabledReason: "Coming soon",
  },
];

interface ExportMenuButtonProps {
  onExport: (format: ExportFormat) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const ExportMenuButton = ({ onExport, disabled = false, sx }: ExportMenuButtonProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("excel");

  const selectedOption = exportOptions.find((o) => o.format === selectedFormat) ?? exportOptions[0];

  const handleMainClick = () => {
    onExport(selectedFormat);
  };

  const handleMenuItemClick = (option: ExportOption) => {
    if (option.disabled) return;
    setSelectedFormat(option.format);
    setOpen(false);
    onExport(option.format);
  };

  return (
    <Box sx={sx}>
      <ButtonGroup ref={anchorRef} variant="outlined" disabled={disabled} sx={{ whiteSpace: "nowrap" }}>
        <MuiButton onClick={handleMainClick} size="small">
          {selectedOption.label}
        </MuiButton>
        <MuiButton size="small" onClick={() => setOpen((prev) => !prev)} sx={{ px: 0.5, minWidth: "unset" }}>
          <ArrowDropDownIcon fontSize="small" />
        </MuiButton>
      </ButtonGroup>

      <Popper open={open} anchorEl={anchorRef.current} transition placement="bottom-end" sx={{ zIndex: 1 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={4} sx={{ mt: 0.5 }}>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <MenuList dense>
                  {exportOptions.map((option) => (
                    <MenuItem
                      key={option.format}
                      selected={option.format === selectedFormat}
                      disabled={option.disabled}
                      onClick={() => handleMenuItemClick(option)}
                    >
                      <ListItemText>
                        {option.label}
                        {option.disabledReason && <> — {option.disabledReason}</>}
                      </ListItemText>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};
